import { createApi } from "@reduxjs/toolkit/query/react";
import { PracticeQuestion } from "@src/pages/question/practice-question/add-practice-question/types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";

export interface GetPracticeQuestionsResponse {
  response: {
    content: PracticeQuestion[];
    last: boolean;
    totalElements?: number;
    totalPages?: number;
    pageNo?: number;
    pageSize?: number;
  };
}

export const PracticeQuestionApiSlice = createApi({
  reducerPath: "practiceQuestionApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["UserCode"],
  endpoints: (builder) => ({
    getAllBatchPracticeQuestion: builder.query<
      GetPracticeQuestionsResponse,
      { batchId: string; pageNo: number; pageSize: number }
    >({
      query: ({ batchId, pageNo, pageSize }) => ({
        url: `practiceQuestions/batch?batchId=${batchId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    addBatchPracticeQuestion: builder.mutation<
      any,
      { payload: any; syllabusId: string | undefined; batchId: string }
    >({
      query: ({ payload, syllabusId, batchId }) => ({
        url: `practiceQuestions?syllabusId=${syllabusId}&batchId=${batchId}`,
        method: "POST",
        body: payload,
      }),
    }),
    updateBatchPracticeQuestion: builder.mutation<
      any,
      {
        payload: any;
        practiceQuestionId: string;
        syllabusId: string;
        batchId: string;
      }
    >({
      query: ({ payload, syllabusId, batchId, practiceQuestionId }) => ({
        url: `practiceQuestions?practiceQuestionId=${practiceQuestionId}&syllabusId=${syllabusId}&batchId=${batchId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getBatchPracticeQuestionById: builder.query<any, { questionId: string }>({
      query: ({ questionId }) => ({
        url: `practiceQuestions/${questionId}`,
        method: "GET",
      }),
    }),
    getAllPracticeQuestionForParent: builder.query<
      GetPracticeQuestionsResponse,
      { parentId: string; trainerId: string; pageNo: number; pageSize: number }
    >({
      query: ({ parentId, trainerId, pageNo, pageSize }) => ({
        url: `practiceQuestions/parent?parentId=${parentId}&trainerId=${trainerId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    addPracticeQuestion: builder.mutation<
      any,
      { payload: any; syllabusId: string }
    >({
      query: ({ payload, syllabusId }) => ({
        url: `practiceQuestions?syllabusId=${syllabusId}`,
        method: "POST",
        body: payload,
      }),
    }),
    updatePracticeQuestion: builder.mutation<
      any,
      { payload: any; practiceQuestionId: string; syllabusId: string }
    >({
      query: ({ payload, syllabusId, practiceQuestionId }) => ({
        url: `practiceQuestions?practiceQuestionId=${practiceQuestionId}&syllabusId=${syllabusId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getAllPracticeQuestionToAssign: builder.query<
      any,
      {
        parentId: string;
        batchId: string;
        pageNo: number;
        pageSize: number;
        parentType: string;
      }
    >({
      query: ({ parentId, batchId, pageNo, pageSize, parentType }) => ({
        url: `practiceQuestions/to-assign?parentId=${parentId}&batchId=${batchId}&parentType=${parentType}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    assignPracticeQuestionToBatch: builder.mutation<
      any,
      {
        payload: string[];
        parentId: string;
        batchId: string;
        parentType: string;
        trainerId: string;
      }
    >({
      query: ({ payload, parentId, batchId, trainerId, parentType }) => ({
        url: `practiceQuestions/assign?parentId=${parentId}&batchId=${batchId}&parentType=${parentType}&trainerId=${trainerId}`,
        method: "POST",
        body: payload,
      }),
    }),
    getAssignedPracticeQuestion: builder.query<
      any,
      {
        batchId: string;
        parentId: string;
        type?:"PRACTICE" | "CODE"
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ batchId, type, parentId, pageNo, pageSize }) => ({
        url: `practiceQuestions/all?batchId=${batchId}&type=${type}&parentId=${parentId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    submitPracticeQuestion: builder.mutation<any, { payload: any }>({
      query: ({ payload }) => ({
        url: `execution/execute-practice-questions`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { payload }) => [
        { type: "UserCode", id: payload?.questionId },
      ],
    }),
 getUserCode: builder.query<
      any,
      {
        practiceQuestionId: string;
        studentId: string;
        batchId: string;
      }
    >({
      query: ({ practiceQuestionId, studentId, batchId }) => ({
        url: `practiceQuestions/get-UserCode?practiceQuestionId=${practiceQuestionId}&studentId=${studentId}&batchId=${batchId}`,
        method: "GET",
      }),
      providesTags: (result, error, { practiceQuestionId }) => [
        { type: "UserCode", id: practiceQuestionId },
      ],
    }),
    checkDuplicatePracticeQuestion: builder.query<
      any,
      { questionTitle: string; parentId: string }
    >({
      query: ({ questionTitle, parentId }) => ({
        url: `practiceQuestions/check-duplicate?questionTitle=${questionTitle}&parentId=${parentId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAllBatchPracticeQuestionQuery,
  useLazyGetAllBatchPracticeQuestionQuery,
  useAddBatchPracticeQuestionMutation,
  useUpdateBatchPracticeQuestionMutation,
  useGetBatchPracticeQuestionByIdQuery,
  useGetAllPracticeQuestionForParentQuery,
  useLazyGetAllPracticeQuestionForParentQuery,
  useAddPracticeQuestionMutation,
  useUpdatePracticeQuestionMutation,
  useGetAllPracticeQuestionToAssignQuery,
  useLazyGetAllPracticeQuestionToAssignQuery,
  useAssignPracticeQuestionToBatchMutation,
  useGetAssignedPracticeQuestionQuery,
  useLazyGetAssignedPracticeQuestionQuery,
  useSubmitPracticeQuestionMutation,
  useLazyCheckDuplicatePracticeQuestionQuery,
  useGetUserCodeQuery,
  useLazyGetUserCodeQuery,
} = PracticeQuestionApiSlice;
